/*
 * Welham & Co: a HELD add to cart, on the product pages that still use
 * Horizon's own buy box (session W3, 2026-09-24). Ported from The Foozie's
 * assets/fz-add-guard.js (session A3), which found the defect; the code below
 * the header is that file's, unchanged, so a fix in either store ports to the
 * other.
 *
 * WHY. Horizon 4.2.0's product-form.js QUEUES an add that is pressed while a
 * variant change is still being fetched, and sends it afterwards as
 * {items: [{id, quantity}]} (#processBatchAddToCart). Every other field on the
 * form is dropped. Here that is the add-ons' properties[Order] (the order the
 * add-on belongs to: combine-photos has five variants, the phone case 31) and
 * the Go Gift Cards recipient fields. The portrait pages do not use Horizon's
 * form at all (plan section 3.3: the brief renders its own), so this loads
 * only on the suffix templates (snippets/wc-head-w3).
 *
 * WHAT. An add whose form carries such a field is held here while a variant
 * change is in flight, then submitted again once that change has landed. By
 * then the product form is idle, so it sends the WHOLE form, exactly as an add
 * pressed a second later would. An add with no such field is left to
 * Horizon's queue, which loses nothing for it. Horizon's own file is not
 * edited: its delegated submit handler listens on document in the capture
 * phase, and this listens on window, which runs first.
 *
 * The top of this file is pure, with no DOM and no imports, so
 * bin/test-wc-add-guard.mjs can drive it under Node. The browser wiring is at
 * the bottom.
 */

/** How long a held add waits for a variant change that never settles, in ms. */
export const HOLD_LIMIT_MS = 5000;

/**
 * True when submitting these form entries sends something Horizon's queued
 * add would drop: a non-empty line-item property or selling plan. Disabled
 * inputs never reach FormData, so an unticked "send as a gift" box does not
 * count.
 * @param {Iterable<[string, FormDataEntryValue]>} entries
 */
export function carriesLineFields(entries) {
  for (const [name, value] of entries) {
    if (typeof value !== "string" || value === "") continue;
    if (name.startsWith("properties[") || name === "selling_plan") return true;
  }
  return false;
}

/**
 * The guard's state and decisions, with the browser injected so the seam can
 * be tested: `formDataOf(form)` for the entries, `nextFrame()` to let the new
 * variant's markup settle, `wait(ms)` for the limit.
 * @param {{
 *   formDataOf: (form: any) => Iterable<[string, FormDataEntryValue]>,
 *   nextFrame: () => Promise<void>,
 *   wait: (ms: number) => Promise<void>,
 *   holdLimitMs?: number,
 * }} deps
 */
export function createAddGuard({ formDataOf, nextFrame, wait, holdLimitMs = HOLD_LIMIT_MS }) {
  /** The newest variant change per scope (a section), until it settles. */
  const inflight = new Map();
  /** Forms with an add already held, so a double press is one add, not two. */
  const held = new Set();

  /**
   * Record a variant change. Only the newest per scope counts: an older one
   * is superseded (and usually aborted) by the newer.
   * @param {any} scope
   * @param {Promise<unknown>} promise
   */
  function track(scope, promise) {
    const settled = Promise.resolve(promise).then(
      () => undefined,
      () => undefined,
    );
    inflight.set(scope, settled);
    settled.then(() => {
      if (inflight.get(scope) === settled) inflight.delete(scope);
    });
  }

  /** Wait until no variant change is in flight in `scope`, or the limit passes. */
  async function idle(scope) {
    const deadline = wait(holdLimitMs).then(() => "timeout");
    for (let current = inflight.get(scope); current; current = inflight.get(scope)) {
      if ((await Promise.race([current, deadline])) === "timeout") return;
      await nextFrame();
    }
  }

  /**
   * Decide one submit. Returns 'pass' (let Horizon handle it now), 'held'
   * (prevented; it will be submitted again) or 'duplicate' (prevented; an add
   * for this form is already held).
   * @param {{ form: any, scope: any, submitter?: any, prevent: () => void, resubmit: (submitter?: any) => void }} submit
   */
  function onSubmit({ form, scope, submitter, prevent, resubmit }) {
    if (held.has(form)) {
      prevent();
      return "duplicate";
    }
    if (!inflight.has(scope)) return "pass";
    if (!carriesLineFields(formDataOf(form))) return "pass";
    prevent();
    held.add(form);
    idle(scope).then(() => {
      held.delete(form);
      resubmit(submitter);
    });
    return "held";
  }

  return { track, onSubmit, isInflight: (/** @type {any} */ scope) => inflight.has(scope) };
}

/* ------------------------------------------ a variant this market can't buy */

/**
 * The variant ids this market cannot buy, from the JSON lists snippets/
 * wc-head-w3 writes (studio.markets_excluded, read through snippets/
 * wc-variant-offered). Luke's Tier 1 range: Canada has no 18×24 framed, and
 * 24×36 framed is US, UK and EU only. An unreadable list adds nothing.
 * @param {Iterable<string>} texts
 */
export function excludedIdsFrom(texts) {
  const ids = new Set();
  for (const text of texts) {
    try {
      for (const id of JSON.parse(text)) ids.add(String(id));
    } catch {
      /* not a list: nothing to exclude */
    }
  }
  return ids;
}

/**
 * True when these form entries would add a variant this market cannot buy.
 * Pickers never offer one, so this refuses a `?variant=`, a script or a forced
 * id: K1's W-G forces the id and calls requestSubmit, and it must not post.
 * @param {Iterable<[string, any]>} entries
 * @param {Set<string>} excluded
 */
export function isExcludedAdd(entries, excluded) {
  if (!excluded.size) return false;
  for (const [key, value] of entries) if (key === "id" && excluded.has(String(value))) return true;
  return false;
}

/* ------------------------------------------------------------- the browser */

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const guard = createAddGuard({
    formDataOf: (form) => new FormData(form),
    nextFrame: () => new Promise((resolve) => requestAnimationFrame(() => resolve(undefined))),
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  });
  /** @param {Element} el */
  const scopeOf = (el) => el.closest(".shopify-section, dialog, product-card");

  import("@shopify/events").then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.productSelect, (/** @type {any} */ event) => {
      const scope = event.target instanceof Element ? scopeOf(event.target) : null;
      if (scope && event.promise) guard.track(scope, event.promise);
    });
  });

  /** @type {Set<string> | null} */
  let excluded = null;
  const excludedIds = () =>
    (excluded ??= excludedIdsFrom(
      Array.from(document.querySelectorAll("script[data-wc-excluded-variants]"), (el) => el.textContent || ""),
    ));

  window.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // Refused before Horizon's own handler sees it (both listen in capture
      // on the way down; this one is on window, above document).
      if (/\/cart\/add/.test(form.getAttribute("action") || "") && isExcludedAdd(new FormData(form), excludedIds())) {
        event.preventDefault();
        event.stopImmediatePropagation();
        form.dataset.wcRefused = "not-offered";
        return;
      }
      if (!form.closest("product-form-component")) return;
      const scope = scopeOf(form);
      if (!scope) return;
      guard.onSubmit({
        form,
        scope,
        submitter: /** @type {SubmitEvent} */ (event).submitter,
        prevent: () => {
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        resubmit: (submitter) => {
          if (form.isConnected) form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined);
        },
      });
    },
    { capture: true },
  );
}
